import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { assetUrl } from "@/lib/api";

const signature = (ann) => {
  try {
    return btoa(unescape(encodeURIComponent(`${ann.title}|${ann.start_date}`))).replace(/=+/g, "").slice(0, 24);
  } catch {
    return "ann";
  }
};

const todayStr = () => new Date().toISOString().slice(0, 10);

function shouldShow(ann) {
  if (!ann || !ann.enabled || !ann.title) return false;
  const today = todayStr();
  if (ann.start_date && today < ann.start_date) return false;
  if (ann.end_date && today > ann.end_date) return false;
  const sig = signature(ann);
  if (ann.frequency === "every") return true;
  if (ann.frequency === "daily") return localStorage.getItem(`ktn_ann_d_${sig}`) !== today;
  return !localStorage.getItem(`ktn_ann_o_${sig}`);
}

function markShown(ann) {
  const sig = signature(ann);
  if (ann.frequency === "daily") localStorage.setItem(`ktn_ann_d_${sig}`, todayStr());
  else if (ann.frequency !== "every") localStorage.setItem(`ktn_ann_o_${sig}`, "1");
}

export default function AnnouncementPopup({ announcement, onOpenPhotoForm, onNavigate }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!shouldShow(announcement)) return undefined;
    const t = setTimeout(() => {
      setOpen(true);
      markShown(announcement);
    }, 1500);
    return () => clearTimeout(t);
  }, [announcement]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!announcement) return null;

  const handleCta = () => {
    setOpen(false);
    if (announcement.button_action === "photo_form") {
      onOpenPhotoForm();
      return;
    }
    const url = announcement.button_url || "";
    if (!url) return;
    if (url.startsWith("#")) onNavigate(url);
    else window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[120] bg-charcoal/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="announcement-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 28 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg max-h-[88vh] bg-ivory text-charcoal flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="announcement-popup"
          >
            <button
              onClick={() => setOpen(false)}
              data-testid="announcement-close"
              aria-label="Close announcement"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-charcoal/60 text-ivory flex items-center justify-center transition-colors duration-300 hover:bg-vermillion"
            >
              <X size={17} strokeWidth={2} />
            </button>

            {announcement.image && (
              <img
                src={assetUrl(announcement.image)}
                alt=""
                data-testid="announcement-image"
                className="w-full aspect-[16/9] object-cover shrink-0"
              />
            )}

            <div className="p-7 md:p-9 overflow-y-auto" data-lenis-prevent>
              <p className="text-[10px] uppercase tracking-[0.35em] text-gold font-semibold">Announcement</p>
              <h3 className="mt-3 font-serif font-semibold tracking-tight text-2xl md:text-3xl leading-tight" data-testid="announcement-title">
                {announcement.title}
              </h3>
              {announcement.description && (
                <p className="mt-5 text-sm md:text-base text-charcoal/70 leading-relaxed whitespace-pre-wrap" data-testid="announcement-description">
                  {announcement.description}
                </p>
              )}
              {announcement.button_text && (
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={handleCta}
                  data-testid="announcement-cta"
                  className="group mt-7 w-full inline-flex items-center justify-center gap-3 bg-vermillion text-ivory text-xs uppercase tracking-[0.22em] font-semibold px-8 py-4 transition-colors duration-300 hover:bg-blood"
                >
                  {announcement.button_text}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </motion.button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
